import { cn, tv } from '@pcln/horizon'
import { MULTI_DESTINATION, ROUND_TRIP } from '../constants'

// Flight type variant definitions
const flightTypeVariants = {
  multiDestination: {
    gridContainer: 'sm:grid-cols-3',
    flightTypeWrapper: 'sm:[grid-area:unset]'
  },
  oneWay: {
    gridContainer: [
      'sm:grid-cols-6 lg:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
      "lg:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_passengers_passengers_cabin_cabin'_'submit_submit_submit_submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  },
  oneWayNoCabin: {
    gridContainer: [
      'sm:grid-cols-6 lg:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
      "lg:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_timeline_passengers_passengers_passengers'_'submit_submit_submit_submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  },
  roundTrip: {
    gridContainer: [
      'sm:grid-cols-6 lg:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
      "lg:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_passengers_passengers_cabin_cabin'_'deals_deals_deals_submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  },
  roundTripNoCabin: {
    gridContainer: [
      'sm:grid-cols-6 lg:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
      "lg:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_timeline_passengers_passengers_passengers'_'deals_deals_deals_submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  },
  // Compact variants - maintain sm layout through lg for homepage hero
  multiDestinationCompact: {
    gridContainer: 'sm:grid-cols-3',
    flightTypeWrapper: 'sm:[grid-area:unset]'
  },
  oneWayCompact: {
    gridContainer: [
      'sm:grid-cols-6',
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_passengers_passengers_cabin_cabin'_'._._._submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  },
  oneWayNoCabinCompact: {
    gridContainer: [
      'sm:grid-cols-6',
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_timeline_passengers_passengers_passengers'_'._._._submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  },
  roundTripCompact: {
    gridContainer: [
      'sm:grid-cols-6',
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_passengers_passengers_cabin_cabin'_'deals_deals_deals_submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  },
  roundTripNoCabinCompact: {
    gridContainer: [
      'sm:grid-cols-6',
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_timeline_passengers_passengers_passengers'_'deals_deals_deals_submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  }
}

const isMultiDestinationVariants = {
  true: {
    passengersInput: 'sm:[grid-area:unset]',
    cabinInput: 'sm:[grid-area:unset]'
  },
  false: {
    passengersInput: 'sm:[grid-area:passengers]',
    cabinInput: 'sm:[grid-area:cabin]'
  }
}

type FlightTypeVariant = keyof typeof flightTypeVariants
// export type IsMultiDestinationVariant = keyof typeof isMultiDestinationVariants

const flightFormSlots = {
  gridContainer: 'grid grid-cols-1 gap-3 items-center mx-auto',
  flightTypeWrapper: [
    'lg:h-9 text-nowrap lg:[grid-area:auto/1/auto/-1] w-max grid gap-4',
    'grid-cols-[max-content_max-content] items-center [grid-area:unset]'
  ],
  inputBase: '[grid-area:unset] **:translate-y-0',
  originInput: '[grid-area:unset] sm:[grid-area:origin]',
  destinationInput: '[grid-area:unset] sm:[grid-area:destination]',
  timelineInput: '[grid-area:unset] sm:[grid-area:timeline] **:translate-y-0',
  passengersInput: '[grid-area:unset] **:translate-y-0',
  cabinInput: '[grid-area:unset]',
  multiDestDateWrapper: '**:translate-y-0',
  packageDeals: 'gap-2 text-sm [grid-area:unset] sm:[grid-area:deals]',
  packageDealsNonCompact:
    'flex flex-row items-center justify-start gap-2 text-sm [grid-area:unset] sm:[grid-area:deals] lg:[grid-area:auto/1/auto/-1] lg:order-last'
}

const flightFormDefaultVariants = {
  flightType: 'oneWay' as const,
  isMultiDestination: false
}

const horizonFlightForm = tv({
  slots: flightFormSlots,
  variants: {
    flightType: flightTypeVariants,
    isMultiDestination: isMultiDestinationVariants
  },
  defaultVariants: flightFormDefaultVariants
})

// Helper function to determine flight type variant
const getFlightTypeVariant = (
  flightType: string,
  hideCabin: boolean,
  useCompactLayout = false
): FlightTypeVariant => {
  if (useCompactLayout) {
    if (flightType === MULTI_DESTINATION) {
      return 'multiDestinationCompact'
    }
    if (flightType === ROUND_TRIP) {
      return hideCabin ? 'roundTripNoCabinCompact' : 'roundTripCompact'
    }
    return hideCabin ? 'oneWayNoCabinCompact' : 'oneWayCompact'
  }

  if (flightType === MULTI_DESTINATION) {
    return 'multiDestination'
  }

  if (flightType === ROUND_TRIP) {
    return hideCabin ? 'roundTripNoCabin' : 'roundTrip'
  }

  return hideCabin ? 'oneWayNoCabin' : 'oneWay'
}

// Utility functions
export const getGridContainerClass = (
  flightType: string,
  addHotel: boolean,
  hideCabin = false,
  useCompactLayout = false
) => {
  const flightTypeVariant = getFlightTypeVariant(
    flightType,
    hideCabin,
    useCompactLayout
  )
  const isMultiDestination = flightType === MULTI_DESTINATION

  const styles = horizonFlightForm({
    flightType: flightTypeVariant,
    isMultiDestination
  })

  return cn(
    styles.gridContainer(),
    addHotel ? 'with-hotel' : 'without-hotel',
    flightType.toLowerCase()
  )
}

export const getFlightTypeClass = (flightType: string) => {
  const flightTypeVariant = getFlightTypeVariant(flightType, false)
  const isMultiDestination = flightType === MULTI_DESTINATION

  const styles = horizonFlightForm({
    flightType: flightTypeVariant,
    isMultiDestination
  })

  return styles.flightTypeWrapper()
}

export const getInputClass = (
  type:
    | 'origin'
    | 'destination'
    | 'timeline'
    | 'passengers'
    | 'cabin'
    | 'multiDestDateWrapper'
    | 'packageDeals',
  flightType = 'OW',
  useCompactLayout = false
) => {
  const isMultiDestination = flightType === MULTI_DESTINATION

  const styles = horizonFlightForm({
    isMultiDestination
  })

  // Handle package deals separately since it depends on useCompactLayout
  if (type === 'packageDeals') {
    return useCompactLayout
      ? flightFormSlots.packageDeals
      : flightFormSlots.packageDealsNonCompact
  }

  const inputMap = {
    origin: styles.originInput,
    destination: styles.destinationInput,
    timeline: styles.timelineInput,
    passengers: styles.passengersInput,
    cabin: styles.cabinInput,
    multiDestDateWrapper: () => flightFormSlots.multiDestDateWrapper
  } as const

  const baseClass = inputMap[type]()

  // In non-compact layout, hide cabin at lg breakpoint (it's shown elsewhere)
  if (type === 'cabin' && !useCompactLayout) {
    return cn(baseClass, 'lg:hidden')
  }

  return baseClass
}
